import React, { useEffect, useState } from "react";
import makeStyles from "@material-ui/styles/makeStyles";
import Dialog from "@material-ui/core/Dialog";
import { DialogTitle } from "../../../components";
import DialogContent from "@material-ui/core/DialogContent";
import TextField from "@material-ui/core/TextField";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import useFromValidator from "../../../common/formValidator";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import red from "@material-ui/core/colors/red";

const schema = {
    quantity: {
        presence: { allowEmpty: false, message: " is required" },
        type: "number",
        numericality: { greaterThanOrEqualTo: 0 },
    },
    costperunit: {
        presence: { allowEmpty: false, message: " is required" },
        type: "number",
        numericality: { greaterThanOrEqualTo: 0 },
    },
    date: {
        presence: { allowEmpty: false, message: " is required" },
    },
    materialTypeIdx: {
        presence: { allowEmpty: false, message: " is required" },
    },
    medicineIdx: {
        presence: { allowEmpty: false, message: " is required" },
    },
    farmIdx: {
        presence: { allowEmpty: false, message: " is required" },
    },
};

const useStyles = makeStyles((theme) => ({
    closeButton: {
        position: "absolute",
        right: theme.spacing(1),
        top: theme.spacing(0),
        color: theme.palette.text.default,
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 160,
    },
    textField: {
        marginTop: theme.spacing(2),
    },
    signInButton: {
        margin: theme.spacing(2, 0),
    },
    progressContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
    },
    progressBar: {
        margin: theme.spacing(2),
        position: "absolute",
        top: "40%",
        zIndex: "9999",
    },
    typo: {
        marginLeft: theme.spacing(0.25),
        marginTop: theme.spacing(-1),
        fontSize: "initial",
    },
    typoRed: {
        marginLeft: theme.spacing(0.25),
        marginTop: theme.spacing(-1),
        fontSize: "initial",
        color: red[900],
    },
}));

const MedicineStorageMoveDialog = ({
    deleteErrorMessage,
    errorMessage,
    onEdit,
    data,
    onSave,
    handleClose,
    showDialog,
    editMode,
    materialTypeList,
    materialList,
    farms,
    availableStorage,
    getAvailableStorage,
}) => {
    const classes = useStyles();
    const [formState, hasError, handleChange, updateData] = useFromValidator(schema);
    const [popupLoading, setPopupLoading] = useState(false);
    const [showQuantityMessage, setShowQuantityMessage] = useState(null);
    const [showNotification, setShowNotification] = useState(null);

    const resetBeforeEdit = () => {
        if (editMode) {
            setShowQuantityMessage(`The quantity should be less than or equal to ${availableStorage}`);
        }
        if (showDialog) {
            setShowQuantityMessage(null);
            setShowNotification(null);
            setPopupLoading(false);
            updateData(data, editMode);
        }
    };
    useEffect(resetBeforeEdit, [showDialog]);

    const handleMaterialTypeChange = (event) => {
        handleChange(event);
    };

    const handleMedicineChange = (event) => {
        const typeIdx = formState.values.materialTypeIdx;
        if (typeIdx === undefined || typeIdx === "") return handleChange(event);
        const typeName = materialTypeList[typeIdx]?.type;
        const materialMasterId = materialList?.[typeName]?.[event.target.value]?.materialMasterId;
        const entryDate = formState.values.date || new Date();
        const storageID = editMode ? (data?.id || 0) : 0;
        if (materialMasterId) {
            getAvailableStorage(materialMasterId, entryDate, storageID);
        }
        handleChange(event);
    };

    const handleDateChange = (date) => {
        const typeIdx = formState.values.materialTypeIdx;
        const medIdx = formState.values.medicineIdx;
        if (typeIdx !== undefined && typeIdx !== "" && medIdx !== undefined && medIdx !== "") {
            const typeName = materialTypeList[typeIdx]?.type;
            const materialMasterId = materialList?.[typeName]?.[medIdx]?.materialMasterId;
            const storageID = editMode ? (data?.id || 0) : 0;
            if (materialMasterId) {
                getAvailableStorage(materialMasterId, date, storageID);
            }
        }
        handleChange({ target: { name: "date", value: date } });
    };

    const handleQuantityChange = (event) => {
        if (event.target.value > availableStorage) {
            setShowQuantityMessage(`The quantity should be less than or equal to ${availableStorage}`);
        } else {
            setShowQuantityMessage(null);
        }
        handleChange(event);
    };

    useEffect(() => {
        if (availableStorage > 0 && (formState.values.quantity < 0 || formState.values.quantity > availableStorage)) {
            setShowNotification(true);
        } else {
            setShowNotification(null);
        }
        setShowQuantityMessage(`The quantity should be less than or equal to ${availableStorage}`);
    }, [formState.values.quantity, availableStorage]);

    const onSubmit = () => {
        setPopupLoading(true);
        if (formState.values.quantity > availableStorage) {
            setPopupLoading(false);
            return;
        }
        const typeIdx = formState.values.materialTypeIdx;
        const medIdx = formState.values.medicineIdx;
        const typeName = materialTypeList[typeIdx]?.type;
        const medicine = materialList?.[typeName]?.[medIdx];
        const farm = farms[formState.values.farmIdx];
        const tempData = {
            ...formState.values,
            materialMasterId: medicine?.materialMasterId,
            materialName: medicine?.materialName,
            materialType: typeName,
            farmId: farm?.id,
            farmName: farm?.name,
        };
        if (editMode) {
            onEdit(tempData, setPopupLoading);
        } else {
            onSave(tempData, setPopupLoading);
        }
    };

    const currentTypeName = materialTypeList?.[formState.values.materialTypeIdx]?.type;
    const currentMedicineList = materialList?.[currentTypeName] || [];

    return (
        <Dialog disableBackdropClick open={showDialog} onClose={handleClose} aria-labelledby="form-dialog-title">
            <DialogTitle id="form-dialog-title">
                {editMode ? "Update" : "New"} Medicine Storage Move
            </DialogTitle>
            <IconButton aria-label="close" className={classes.closeButton} onClick={handleClose}>
                <CloseIcon />
            </IconButton>
            <DialogContent>
                {popupLoading && (
                    <div className={classes.progressContainer}>
                        <CircularProgress className={classes.progressBar} />
                    </div>
                )}
                <div id="error">
                    {errorMessage && (
                        <Chip onDelete={deleteErrorMessage} color="primary" label={errorMessage} />
                    )}
                </div>
                <br />
                <InputLabel
                    className={formState.values.quantity > availableStorage ? classes.typoRed : classes.typo}
                >
                    {showNotification && (
                        <Chip
                            color="primary"
                            size="small"
                            onDelete={() => setShowNotification(null)}
                            label={showQuantityMessage}
                        />
                    )}
                </InputLabel>

                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <DatePicker
                        className={classes.textField}
                        autoOk
                        variant="inline"
                        inputVariant="outlined"
                        label="Date"
                        format="dd/MM/yyyy"
                        value={formState.values.date || null}
                        onChange={handleDateChange}
                        animateYearScrolling
                        name="date"
                        style={{ backgroundColor: "#ffffff" }}
                    />
                </MuiPickersUtilsProvider>

                <FormControl className={classes.formControl}>
                    <InputLabel id="material-type-label">Material Type</InputLabel>
                    <Select
                        id="materialType"
                        value={formState.values.materialTypeIdx !== undefined ? formState.values.materialTypeIdx : ""}
                        onChange={(e) => handleChange(e, handleMaterialTypeChange(e))}
                        name="materialTypeIdx"
                    >
                        {materialTypeList?.map((mt, idx) => (
                            <MenuItem key={mt.value} value={idx}>{mt.type}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl className={classes.formControl}>
                    <InputLabel id="medicine-label">Medicine / Vaccination</InputLabel>
                    <Select
                        id="medicineIdx"
                        value={formState.values.medicineIdx !== undefined ? formState.values.medicineIdx : ""}
                        onChange={(e) => handleChange(e, handleMedicineChange(e))}
                        name="medicineIdx"
                    >
                        {currentMedicineList.map((m, idx) => (
                            <MenuItem key={m.materialMasterId} value={idx}>{m.materialName}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <InputLabel>
                    {formState.values.medicineIdx !== undefined &&
                        formState.values.medicineIdx !== "" &&
                        availableStorage !== null
                        ? `Available Stock: ${availableStorage}`
                        : ""}
                </InputLabel>

                <FormControl className={classes.formControl}>
                    <InputLabel id="farm-label">Farm</InputLabel>
                    <Select
                        id="farmIdx"
                        value={formState.values.farmIdx !== undefined ? formState.values.farmIdx : ""}
                        onChange={(e) => handleChange(e)}
                        name="farmIdx"
                    >
                        {farms?.map((farm, idx) => (
                            <MenuItem key={farm.id} value={idx}>{farm.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    className={classes.textField}
                    error={hasError("quantity")}
                    fullWidth
                    helperText={hasError("quantity") ? formState.errors.quantity[0] : null}
                    label="Quantity"
                    name="quantity"
                    onChange={handleQuantityChange}
                    type="number"
                    value={formState.values.quantity || ""}
                    variant="outlined"
                    style={{ backgroundColor: "#ffffff" }}
                />

                <TextField
                    className={classes.textField}
                    error={hasError("costperunit")}
                    fullWidth
                    helperText={hasError("costperunit") ? formState.errors.costperunit[0] : null}
                    label="Cost per unit"
                    name="costperunit"
                    onChange={handleChange}
                    type="number"
                    value={formState.values.costperunit || ""}
                    variant="outlined"
                    style={{ backgroundColor: "#ffffff" }}
                />

                <DialogActions>
                    <Button
                        className={classes.signInButton}
                        color="primary"
                        size="large"
                        type="submit"
                        variant="contained"
                        disabled={!formState.isValid}
                        onClick={onSubmit}
                    >
                        Save
                    </Button>
                </DialogActions>
            </DialogContent>
        </Dialog>
    );
};

export default MedicineStorageMoveDialog;
